import { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { useCurrentUser, userIsRealAppAdmin } from "@/lib/auth";
import {
  AdminActionCancelled,
  clearTestData,
  cloneProductionData,
  loadDemoData,
  publishProduction,
} from "@/lib/adminDevActions";
import { devToolsVisibility } from "@/pages/stammdaten/settings/devToolsVisibility";
import { errorMessage } from "@/pages/stammdaten/settings/tenantSettingsTypes";

const TOOLS_HREF = "/stammdaten/einstellungen?tab=testsystem";

/**
 * Kopfzeilen-Menü für die Admin-Werkzeuge, die sonst nur im Tab
 * „Admin“ der Buchungsregeln liegen.
 */
export function AdminDevToolsMenu() {
  const { user } = useCurrentUser();
  const visibility = devToolsVisibility();
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeError, setNoticeError] = useState(false);

  if (!visibility.any || !userIsRealAppAdmin(user)) return null;

  async function run(id: string, action: () => Promise<string>) {
    setBusy(id);
    setNotice(null);
    setNoticeError(false);
    try {
      const message = await action();
      setNotice(message);
      window.alert(message);
    } catch (err) {
      if (err instanceof AdminActionCancelled) return;
      const message = errorMessage(err, "Aktion fehlgeschlagen");
      setNoticeError(true);
      setNotice(message);
      window.alert(message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        type="button"
        className="inline-flex rounded-full border border-separator bg-accent-soft px-2.5 py-1 text-xs font-bold text-label transition hover:bg-fill"
        title="Admin-Werkzeuge: Production veröffentlichen, Demodaten, Testdaten"
      >
        Admin
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-1.5rem))] origin-top-right rounded-ios-lg border border-separator bg-card p-2 shadow-ios-md focus:outline-none">
          <p className="px-2 pb-2 text-xs text-label-tertiary">
            Werkzeuge für Entwicklung und Schulung
          </p>
          <div className="flex flex-col gap-0.5">
            {visibility.showPublishProduction ? (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    disabled={busy != null}
                    className={itemClass(active)}
                    onClick={() => void run("publish", publishProduction)}
                  >
                    {busy === "publish" ? "Wird gestartet…" : "Auf Production veröffentlichen"}
                  </button>
                )}
              </Menu.Item>
            ) : null}
            {visibility.showProductionClone ? (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    disabled={busy != null}
                    className={itemClass(active)}
                    onClick={() => void run("clone", cloneProductionData)}
                  >
                    {busy === "clone" ? "Wird geladen…" : "Produktionsdaten laden"}
                  </button>
                )}
              </Menu.Item>
            ) : null}
            {visibility.showClearTestData ? (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    disabled={busy != null}
                    className={itemClass(active, true)}
                    onClick={() => void run("clear", clearTestData)}
                  >
                    {busy === "clear" ? "Wird gelöscht…" : "Alle Testdaten löschen"}
                  </button>
                )}
              </Menu.Item>
            ) : null}
            {visibility.showDemoSeedTools ? (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    disabled={busy != null}
                    className={itemClass(active)}
                    onClick={() => void run("demo", () => loadDemoData({ reset: false }))}
                  >
                    {busy === "demo" ? "Wird geladen…" : "Demodaten laden"}
                  </button>
                )}
              </Menu.Item>
            ) : null}
            <Menu.Item>
              {({ active }) => (
                <Link to={TOOLS_HREF} className={itemClass(active)}>
                  Alle Werkzeuge…
                </Link>
              )}
            </Menu.Item>
          </div>
          {notice ? (
            <p
              className={clsx(
                "mt-2 px-2 text-xs",
                noticeError ? "text-status-red" : "text-status-green",
              )}
            >
              {notice}
            </p>
          ) : null}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function itemClass(active: boolean, danger = false): string {
  return clsx(
    "rounded-lg px-2 py-1.5 text-left text-sm transition",
    danger ? "text-status-red" : "text-label",
    active && "bg-fill",
    "disabled:opacity-60",
  );
}
